import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Streamer, AnimationAssignment,
  getAnimationAssignments, triggerManualAnimation, setAnimationVolume, refreshOverlay,
  sendDynamicText,
} from './api';
import QuestionQueuePanel from './QuestionQueuePanel';
import RafflePanel from './RafflePanel';

interface Props {
  streamer: Streamer;
  onClose: () => void;
}

function typeIcon(type: string) {
  if (type === 'audio') return '🔊';
  if (type === 'text') return '📝';
  if (type === 'text_dynamic') return '⌨️';
  return '🎬';
}

function accentForType(type: string) {
  if (type === 'audio') return '#22c55e';
  if (type === 'text') return '#f97316';
  if (type === 'text_dynamic') return '#a855f7';
  return '#3b82f6';
}

function volKey(streamerId: number, animId: number) {
  return `mixing_vol_${streamerId}_${animId}`;
}

function readVol(streamerId: number, animId: number): number {
  const v = localStorage.getItem(volKey(streamerId, animId));
  return v !== null ? parseFloat(v) : 1.0;
}

function saveVol(streamerId: number, animId: number, v: number) {
  localStorage.setItem(volKey(streamerId, animId), String(v));
}

// ── Pad auto (gift-triggered assignments) ─────────────────────────────────
function AutoPadCard({
  slot, isActive, onTrigger, volume, onVolumeChange,
}: {
  slot: AnimationAssignment;
  isActive: boolean;
  onTrigger: () => void;
  volume: number;
  onVolumeChange: (v: number) => void;
}) {
  const accent = accentForType(slot.animation_type);
  const icon = typeIcon(slot.animation_type);
  return (
    <div
      onClick={onTrigger}
      style={{
        background: isActive ? `${accent}22` : '#1e2433',
        border: `2px solid ${isActive ? accent : accent + '44'}`,
        borderRadius: 10,
        padding: '10px 8px 8px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: isActive ? `0 0 16px ${accent}88` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 26, textAlign: 'center' }}>{icon}</div>
      <div style={{
        color: isActive ? accent : '#cbd5e1',
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.5, textAlign: 'center',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {slot.name}
      </div>
      {slot.gift_name && (
        <div style={{
          color: '#64748b', fontSize: 9, textAlign: 'center',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {slot.gift_name === '__follow__' ? '🔔 Nouvel abonné' : `🎁 ${slot.gift_name}`}
        </div>
      )}
      {(slot.animation_type === 'audio' || slot.animation_type === 'video') ? (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
          <span style={{ fontSize: 9 }}>{volume === 0 ? '🔇' : '🔊'}</span>
          <input
            type="range" min={0} max={1} step={0.05} value={volume}
            onChange={e => onVolumeChange(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor: accent, cursor: 'pointer', height: 2 } as React.CSSProperties}
          />
          <span style={{ color: '#64748b', fontSize: 8, width: 22, textAlign: 'right', flexShrink: 0 }}>
            {Math.round(volume * 100)}%
          </span>
        </div>
      ) : slot.animation_type === 'text_dynamic' ? (
        <div style={{ color: accent, fontSize: 8.5, textAlign: 'center', marginTop: 2, fontWeight: 700 }}>
          ✎ Clic = saisir le texte
        </div>
      ) : null}
    </div>
  );
}

// ── Pad manuel ────────────────────────────────────────────────────────────
function ManualPadCard({
  slot, isActive, onTrigger, volume, onVolumeChange,
}: {
  slot: AnimationAssignment;
  isActive: boolean;
  onTrigger: () => void;
  volume: number;
  onVolumeChange: (v: number) => void;
}) {
  const accent = accentForType(slot.animation_type);
  const icon = typeIcon(slot.animation_type);
  return (
    <div
      onClick={onTrigger}
      style={{
        background: isActive ? `${accent}22` : '#1e2433',
        border: `2px solid ${isActive ? accent : accent + '44'}`,
        borderRadius: 10,
        padding: '10px 8px 8px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: isActive ? `0 0 16px ${accent}88` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 26, textAlign: 'center' }}>{icon}</div>
      <div style={{
        color: isActive ? accent : '#cbd5e1',
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.5, textAlign: 'center',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {slot.name}
      </div>
      {(slot.animation_type === 'audio' || slot.animation_type === 'video') ? (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
          <span style={{ fontSize: 9 }}>{volume === 0 ? '🔇' : '🔊'}</span>
          <input
            type="range" min={0} max={1} step={0.05} value={volume}
            onChange={e => onVolumeChange(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor: accent, cursor: 'pointer', height: 2 } as React.CSSProperties}
          />
          <span style={{ color: '#64748b', fontSize: 8, width: 22, textAlign: 'right', flexShrink: 0 }}>
            {Math.round(volume * 100)}%
          </span>
        </div>
      ) : slot.animation_type === 'text_dynamic' ? (
        <div style={{ color: accent, fontSize: 8.5, textAlign: 'center', marginTop: 2, fontWeight: 700 }}>
          ✎ Clic = saisir le texte
        </div>
      ) : null}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────
export default function MixingTableFullscreen({ streamer, onClose }: Props) {
  const [assignments, setAssignments] = useState<AnimationAssignment[]>([]);
  const [volumes, setVolumes] = useState<Record<number, number>>({});
  const [activePads, setActivePads] = useState<Set<number>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [dynEditor, setDynEditor] = useState<AnimationAssignment | null>(null);
  const [dynText, setDynText] = useState('');
  const [dynColor, setDynColor] = useState('#ffffff');
  const [dynSize, setDynSize] = useState(48);
  const [dynEffect, setDynEffect] = useState('fade');
  const [dynDuration, setDynDuration] = useState(5);
  const [dynSending, setDynSending] = useState(false);
  const handleRefreshOverlay = useCallback(async () => {
    setRefreshing(true); setRefreshMsg('');
    try {
      const r = await refreshOverlay(streamer.id);
      setRefreshMsg(r.data.pc_connected ? '✓ Overlay rafraîchi' : '⚠️ Overlay non connecté (OBS/TikTok Studio fermé ?)');
    } catch {
      setRefreshMsg('Erreur');
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(''), 4000);
    }
  }, [streamer.id]);
  const wsRef = useRef<WebSocket | null>(null);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  // Load assignments for the active theme
  useEffect(() => {
    if (!streamer.active_theme_id) return;
    getAnimationAssignments(streamer.id, streamer.active_theme_id).then(r => {
      const data = r.data;
      setAssignments(data);
      // init volumes from localStorage
      const vols: Record<number, number> = {};
      data.forEach(a => {
        vols[a.theme_animation_id] = a.volume != null ? a.volume : readVol(streamer.id, a.theme_animation_id);
      });
      setVolumes(vols);
    });
  }, [streamer.id, streamer.active_theme_id]);

  // WebSocket: glow pad when gift fires an animation
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const ws = new WebSocket(`${proto}//${host}/ws/mobile/${streamer.id}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const animId: number | undefined = data.animation_id ?? data.theme_animation_id;
        if (!animId) return;
        flashPad(animId);
      } catch {}
    };
    return () => ws.close();
  }, [streamer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flashPad = useCallback((animId: number) => {
    setActivePads(prev => { const next = new Set(Array.from(prev)); next.add(animId); return next; });
    if (timersRef.current.has(animId)) clearTimeout(timersRef.current.get(animId)!);
    const t = setTimeout(() => {
      setActivePads(prev => { const next = new Set(Array.from(prev)); next.delete(animId); return next; });
      timersRef.current.delete(animId);
    }, 2000);
    timersRef.current.set(animId, t);
  }, []);

  const handleTrigger = useCallback(async (a: AnimationAssignment) => {
    if (a.animation_type === 'text_dynamic') {
      setDynEditor(a);
      setDynText(a.text_content ?? '');
      setDynColor(a.text_color ?? '#ffffff');
      setDynSize(a.text_size ?? 48);
      setDynEffect(a.text_effect ?? 'fade');
      setDynDuration(Math.round((a.text_duration ?? 5000) / 1000));
      return;
    }
    if (a.animation_type === 'raffle') {
      flashPad(a.theme_animation_id);
      return;
    }
    const vol = volumes[a.theme_animation_id] ?? 1.0;
    flashPad(a.theme_animation_id);
    try { await triggerManualAnimation(streamer.id, a.theme_animation_id, vol); } catch {}
  }, [volumes, streamer.id, flashPad]);

  const handleSendDyn = useCallback(async () => {
    if (!dynEditor || !dynText.trim()) return;
    setDynSending(true);
    try {
      await sendDynamicText(streamer.id, {
        text: dynText.trim(),
        text_color: dynColor,
        text_size: dynSize,
        text_font: dynEditor.text_font ?? 'sans-serif',
        text_effect: dynEffect,
        text_loops: dynEditor.text_loops ?? 1,
        text_duration: dynDuration * 1000,
        text_bg_color: '#000000',
        text_bg_opacity: 0,
        text_bg_anim: 'none',
        pos_x: dynEditor.pos_x,
        pos_y: dynEditor.pos_y,
        pos_w: dynEditor.pos_w,
        pos_h: dynEditor.pos_h,
      });
      flashPad(dynEditor.theme_animation_id);
      setDynEditor(null);
      setDynText('');
    } catch {}
    setDynSending(false);
  }, [dynEditor, dynText, dynColor, dynSize, dynEffect, dynDuration, streamer.id, flashPad]);

  const volSaveTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const handleVolumeChange = useCallback((animId: number, v: number) => {
    setVolumes(prev => ({ ...prev, [animId]: v }));
    saveVol(streamer.id, animId, v); // cache local immédiat
    // Persistance serveur (debounced) → s'applique aussi aux cadeaux
    const timers = volSaveTimers.current;
    if (timers.has(animId)) clearTimeout(timers.get(animId)!);
    timers.set(animId, setTimeout(() => {
      setAnimationVolume(animId, v).catch(() => {});
      timers.delete(animId);
    }, 400));
  }, [streamer.id]);

  const autoAssignments = assignments.filter(a => a.trigger_type === 'auto');
  const manualAssignments = assignments.filter(a => a.trigger_type === 'manual');

  const BASE = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
  const iframeUrl = `${BASE}/nsstream/${streamer.id}`;

  const panelStyle: React.CSSProperties = {
    width: 220,
    flexShrink: 0,
    background: '#111827',
    overflowY: 'auto',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const sectionTitle: React.CSSProperties = {
    color: '#64748b',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: '1px solid #1e2d3d',
  };

  const emptyNote: React.CSSProperties = {
    color: '#374151',
    fontSize: 12,
    textAlign: 'center',
    padding: '16px 0',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      background: '#0d1117',
    }}>
      {/* Header */}
      <div style={{
        height: 48, flexShrink: 0,
        background: '#111827',
        borderBottom: '1px solid #1e2d3d',
        display: 'flex', alignItems: 'center', paddingInline: 16, gap: 12,
      }}>
        <span style={{ fontSize: 18 }}>🎛️</span>
        <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, flex: 1 }}>
          Table de Mixage — {streamer.display_name}
        </span>
        {!streamer.active_theme_id && (
          <span style={{ color: '#ef4444', fontSize: 12, marginRight: 8 }}>
            ⚠️ Aucun thème actif
          </span>
        )}
        {refreshMsg && (
          <span style={{ color: refreshMsg.startsWith('✓') ? '#22c55e' : '#f59e0b', fontSize: 12, marginRight: 8 }}>
            {refreshMsg}
          </span>
        )}
        <button
          onClick={handleRefreshOverlay}
          disabled={refreshing}
          title="Recharge l'overlay du streamer (TikTok Studio / OBS) pour appliquer les changements de cadeaux/thème"
          style={{
            background: 'transparent', border: '1px solid #374151',
            color: '#94a3b8', borderRadius: 6, padding: '4px 12px',
            cursor: refreshing ? 'wait' : 'pointer', fontSize: 13,
            marginRight: 8, transition: 'all 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#7c3aed', e.currentTarget.style.color = '#a855f7')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = '#374151', e.currentTarget.style.color = '#94a3b8')}
        >
          {refreshing ? '⏳…' : '🔄 Rafraîchir l’overlay'}
        </button>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: '1px solid #374151',
            color: '#94a3b8', borderRadius: 6, padding: '4px 12px',
            cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#ef4444', e.currentTarget.style.color = '#ef4444')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = '#374151', e.currentTarget.style.color = '#94a3b8')}
        >
          ✕ Fermer
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left — Auto pads */}
        <div style={panelStyle}>
          <div style={sectionTitle}>⚡ Actions auto (cadeaux)</div>
          {autoAssignments.length === 0 ? (
            <div style={emptyNote}>Aucune action auto configurée</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {autoAssignments.map(a => (
                a.animation_type === 'queue' ? (
                  <div key={a.theme_animation_id}>
                    <AutoPadCard
                      slot={a}
                      isActive={activePads.has(a.theme_animation_id)}
                      onTrigger={() => handleTrigger(a)}
                      volume={volumes[a.theme_animation_id] ?? 1.0}
                      onVolumeChange={v => handleVolumeChange(a.theme_animation_id, v)}
                    />
                    <div style={{ marginTop: 8 }}>
                      <QuestionQueuePanel streamerId={streamer.id} />
                    </div>
                  </div>
                ) : a.animation_type === 'raffle' ? (
                  <div key={a.theme_animation_id}>
                    <AutoPadCard
                      slot={a}
                      isActive={activePads.has(a.theme_animation_id)}
                      onTrigger={() => handleTrigger(a)}
                      volume={volumes[a.theme_animation_id] ?? 1.0}
                      onVolumeChange={v => handleVolumeChange(a.theme_animation_id, v)}
                    />
                    <div style={{ marginTop: 8 }}>
                      <RafflePanel streamerId={streamer.id} animationId={a.theme_animation_id} mode={a.text_effect} />
                    </div>
                  </div>
                ) : (
                  <AutoPadCard
                    key={a.theme_animation_id}
                    slot={a}
                    isActive={activePads.has(a.theme_animation_id)}
                    onTrigger={() => handleTrigger(a)}
                    volume={volumes[a.theme_animation_id] ?? 1.0}
                    onVolumeChange={v => handleVolumeChange(a.theme_animation_id, v)}
                  />
                )
              ))}
            </div>
          )}
        </div>

        {/* Center — Live iframe */}
        <div style={{
          flex: 1,
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <iframe
            src={iframeUrl}
            title="Overlay live"
            style={{
              aspectRatio: '9/16',
              height: 'calc(100vh - 48px)',
              width: 'auto',
              border: 'none',
              display: 'block',
            }}
            allow="autoplay"
          />
        </div>

        {/* Right — Manual pads */}
        <div style={panelStyle}>
          <div style={sectionTitle}>🎯 Actions manuelles</div>
          {manualAssignments.length === 0 ? (
            <div style={emptyNote}>Aucune action manuelle configurée</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {manualAssignments.map(a => (
                <ManualPadCard
                  key={a.theme_animation_id}
                  slot={a}
                  isActive={activePads.has(a.theme_animation_id)}
                  onTrigger={() => handleTrigger(a)}
                  volume={volumes[a.theme_animation_id] ?? 1.0}
                  onVolumeChange={v => handleVolumeChange(a.theme_animation_id, v)}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* ══ Éditeur de texte dynamique ══ */}
      {dynEditor && (
        <div
          onClick={() => !dynSending && setDynEditor(null)}
          style={{
            position: 'absolute', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 460, maxWidth: '92vw', background: '#111827',
              border: '1px solid #a855f744', borderRadius: 14,
              padding: 18, boxShadow: '0 0 40px rgba(168,85,247,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}>⌨️</span>
              <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, flex: 1 }}>
                Texte dynamique — {dynEditor.name}
              </span>
            </div>
            <textarea
              value={dynText}
              onChange={e => setDynText(e.target.value)}
              placeholder="Saisir le texte à afficher sur l'overlay…"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box', background: '#0d1117',
                border: '1px solid #1e2d3d', borderRadius: 10, color: '#fff',
                fontSize: 15, padding: '10px 12px', resize: 'vertical', minHeight: 70,
                fontFamily: 'system-ui, sans-serif', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ color: '#64748b', fontSize: 11 }}>Couleur</span>
                <input type="color" value={dynColor} onChange={e => setDynColor(e.target.value)}
                  style={{ width: 34, height: 30, padding: 2, border: '1px solid #1e2d3d', borderRadius: 7, cursor: 'pointer', background: '#0d1117' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ color: '#64748b', fontSize: 11 }}>Taille</span>
                <input type="number" value={dynSize} min={16} max={200} onChange={e => setDynSize(Number(e.target.value))}
                  style={{ width: 56, padding: '6px', borderRadius: 7, border: '1px solid #1e2d3d', background: '#0d1117', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ color: '#64748b', fontSize: 11 }}>Durée</span>
                <input type="number" value={dynDuration} min={1} max={60} step={1} onChange={e => setDynDuration(Number(e.target.value))}
                  style={{ width: 46, padding: '6px', borderRadius: 7, border: '1px solid #1e2d3d', background: '#0d1117', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }} />
                <span style={{ color: '#64748b', fontSize: 11 }}>s</span>
              </label>
              <select value={dynEffect} onChange={e => setDynEffect(e.target.value)}
                style={{ flex: 1, minWidth: 110, padding: '6px', borderRadius: 7, border: '1px solid #1e2d3d', background: '#1e293b', color: '#fff', fontSize: 12, outline: 'none' }}>
                <option value="fade">Fondu</option>
                <option value="scroll-left">Défilement gauche</option>
                <option value="scroll-right">Défilement droite</option>
                <option value="scroll-up">Défilement haut</option>
                <option value="scroll-down">Défilement bas</option>
                <option value="typewriter">Machine à écrire</option>
                <option value="bounce">Rebond</option>
                <option value="zoom">Zoom</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setDynEditor(null)} disabled={dynSending} style={{
                flex: 1, padding: '10px', borderRadius: 9, border: '1px solid #374151',
                background: 'transparent', color: '#94a3b8', fontSize: 13,
                cursor: dynSending ? 'not-allowed' : 'pointer',
              }}>Annuler</button>
              <button onClick={handleSendDyn} disabled={dynSending || !dynText.trim()} style={{
                flex: 2, padding: '10px', borderRadius: 9, border: 'none',
                background: dynSending || !dynText.trim() ? 'rgba(168,85,247,0.35)' : '#a855f7',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: dynSending || !dynText.trim() ? 'not-allowed' : 'pointer',
              }}>{dynSending ? 'Envoi…' : '▶ Envoyer sur l’overlay'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
